/**
 * DTC AI Insights Panel - Direct-to-Consumer Insights
 * ====================================================
 * PHASE 3 UPDATE: Now supports Brain-powered unified insights.
 * When USE_BRAIN_FOR_INSIGHTS=true, fetches from /api/brain/findings/unified
 * 
 * Shows AI-powered insights for DTC platforms (Shopify, Amazon, Walmart)
 * 
 * KEY DIFFERENCES FROM RETAIL AIInsightsPanel:
 * - Uses calendar dates instead of fiscal weeks
 * - Uses platform_id instead of retailer_id
 * - Platform badges: Shopify (green), Amazon (orange), Walmart (blue)
 * - DTC-specific insight categories: revenue_growth, customer_retention, profitability, platform_performance, acquisition
 */

import { useState, useEffect, useCallback } from 'react';
import { Sparkles, TrendingUp, AlertTriangle, Users, DollarSign, Brain, Package, Target, Mail, RefreshCw, X, ChevronDown, Zap } from 'lucide-react';

// Check if Brain-powered insights are enabled
const USE_BRAIN_FOR_INSIGHTS = import.meta.env.VITE_USE_BRAIN_FOR_INSIGHTS === 'true';
import { apiClient } from '../lib/api-client';
import { useTenant } from '../contexts/TenantContext';

interface DtcInsight {
  id: string;
  platform_id?: string;
  date_range_start: string;
  date_range_end: string;
  insight_type: string;  // 'opportunity', 'risk', 'insight', 'alert'
  category: string;       // 'revenue_growth', 'customer_retention', 'profitability', 'platform_performance', 'acquisition'
  priority: string;       // 'critical', 'high', 'medium', 'low'
  title: string;
  description: string;
  recommendation: string;
  dollar_impact: number | null;
  confidence_score: number | null;
  source: string;
  platform?: {
    name: string;
    type: string;
  };
  related_sku?: string;
  related_customer_id?: string;
}

interface DtcAIInsightsPanelProps {
  platformId?: string | null;  // Filter by platform, or null for all platforms
  startDate: string;            // YYYY-MM-DD
  endDate: string;              // YYYY-MM-DD
  category?: string;            // Filter by specific category
  maxInsights?: number;
  compact?: boolean;
  useBrain?: boolean;          // Override to force Brain source (default: auto from env)
}

export default function DtcAIInsightsPanel({
  platformId,
  startDate,
  endDate,
  category,
  maxInsights = 10,
  compact = false,
  useBrain
}: DtcAIInsightsPanelProps) {
  // Determine if we should use Brain-powered insights
  const shouldUseBrain = useBrain ?? USE_BRAIN_FOR_INSIGHTS;
  const [insights, setInsights] = useState<DtcInsight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [autoGenerateAttempted, setAutoGenerateAttempted] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState<DtcInsight | null>(null);
  const [isExpanded, setIsExpanded] = useState(false); // Start collapsed with counter visible

  // Platform colors (matching DTC Overview page)
  const platformColors: Record<string, { primary: string; light: string; medium: string }> = {
    shopify: { primary: '#95BF47', light: '#F0F7E8', medium: '#C5E09B' },
    amazon_seller_central: { primary: '#FF9900', light: '#FFF3E0', medium: '#FFD699' },
    walmart: { primary: '#0071CE', light: '#E3F2FD', medium: '#90CAF9' }
  };

  const fetchInsights = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // PHASE 3: Use Brain-powered unified API when enabled
      if (shouldUseBrain) {
        console.log('[DtcAIInsightsPanel] Using Brain-powered unified API');
        const params = new URLSearchParams();
        params.append('source', 'dtc');
        params.append('limit', maxInsights.toString());
        
        if (platformId && platformId !== 'all') {
          params.append('platformId', platformId);
        }
        
        const response = await apiClient.get<{ 
          ok: boolean; 
          insights: Array<{
            id: string;
            type: string;
            category: string;
            priority: string;
            title: string;
            description: string;
            recommendation?: string;
            dollarImpact: number | null;
            confidenceScore: number | null;
            source: string;
            platformId?: string;
            platformName?: string;
            isBrainPowered: boolean;
          }>;
        }>(`/brain/findings/unified?${params.toString()}`);
        
        if (response.ok && response.insights) {
          // Convert Brain format to DtcInsight format for UI compatibility
          const convertedInsights: DtcInsight[] = response.insights.map(bi => ({
            id: bi.id,
            platform_id: bi.platformId,
            date_range_start: startDate,
            date_range_end: endDate,
            insight_type: bi.type,
            category: bi.category,
            priority: bi.priority,
            title: bi.title,
            description: bi.description,
            recommendation: bi.recommendation || '',
            dollar_impact: bi.dollarImpact,
            confidence_score: bi.confidenceScore,
            source: bi.isBrainPowered ? 'brain_powered' : bi.source,
            platform: bi.platformName ? { name: bi.platformName, type: bi.platformId || '' } : undefined,
          }));
          
          setInsights(convertedInsights);
          console.log(`[DtcAIInsightsPanel] Brain: Loaded ${convertedInsights.length} insights`);
        } else {
          setInsights([]);
        }
        setIsLoading(false);
        return;
      }
      
      // LEGACY PATH: Use original /api/insights/unified endpoint
      const params = new URLSearchParams();
      params.append('source_type', 'dtc');
      params.append('limit', maxInsights.toString());
      if (category) {
        params.append('category', category);
      }

      console.log('[DtcAIInsightsPanel] ONE SOURCE OF TRUTH - Fetching from /api/insights/unified:', { platformId, category });
      const data = await apiClient.get<{ insights: DtcInsight[]; total: number }>(`/insights/unified?${params.toString()}`);

      if (data.insights && Array.isArray(data.insights)) {
        // If platformId is specified, filter client-side (unified returns all DTC)
        let filteredInsights = data.insights;
        if (platformId && platformId !== 'all') {
          filteredInsights = data.insights.filter(i => i.platform_id === platformId);
        }
        setInsights(filteredInsights);
        console.log('[DtcAIInsightsPanel] ONE SOURCE OF TRUTH - Loaded', filteredInsights.length, 'insights (total:', data.insights.length, ')');
      } else {
        setInsights([]);
      }
    } catch (error) {
      console.error('[DtcAIInsightsPanel] Error fetching insights:', error);
      setInsights([]);
    } finally {
      setIsLoading(false);
    }
  }, [platformId, category, maxInsights, shouldUseBrain, startDate, endDate]);

  useEffect(() => {
    // Unified API handles date filtering internally (last 30 days)
    fetchInsights();
  }, [fetchInsights]);

  const generateInsights = useCallback(async () => {
    setIsGenerating(true);
    try {
      console.log('[DtcAIInsightsPanel] Generating insights for', startDate, 'to', endDate);
      
      const response = await apiClient.post<{ success: boolean; insightsGenerated: number }>('/insights/dtc-generate', {
        startDate,
        endDate,
        platformId: platformId === 'all' ? null : platformId,
        force: false
      });

      console.log('[DtcAIInsightsPanel] Generation response:', response);
      if (response.success) {
        await fetchInsights();
      }
    } catch (error) {
      console.error('[DtcAIInsightsPanel] Error generating insights:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [startDate, endDate, platformId, fetchInsights]);

  // Auto-generate insights on first load if none exist
  useEffect(() => {
    if (!isLoading && insights.length === 0 && !isGenerating && !autoGenerateAttempted) {
      console.log('[DtcAIInsightsPanel] No insights found - auto-generating');
      setAutoGenerateAttempted(true);
      generateInsights();
    }
  }, [isLoading, insights.length, isGenerating, autoGenerateAttempted, generateInsights]);

  // Get platform badge color
  const getPlatformColor = (platformType?: string) => {
    if (!platformType) return platformColors.shopify; // Default
    return platformColors[platformType] || platformColors.shopify;
  };

  // Get icon by category
  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'revenue_growth': return TrendingUp;
      case 'customer_retention': return Users;
      case 'profitability': return DollarSign;
      case 'platform_performance': return Package;
      case 'acquisition': return Target;
      default: return Sparkles;
    }
  };

  // Get priority styling
  const getPriorityStyle = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3">
          <Brain className="w-6 h-6 text-purple-600 animate-pulse" />
          <span className="text-gray-600">Loading insights...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 shadow-md">
        {/* Header - Clickable to expand/collapse */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-6 py-4 border-b border-purple-200 bg-white/50 backdrop-blur-sm hover:bg-white/70 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg shadow-md">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-bold text-gray-900">PatternIQ Insights</h3>
                <p className="text-xs text-gray-600">
                  {isLoading ? 'Loading...' : 'Analytics-powered recommendations'}
                </p>
              </div>
              {/* Prominent counter badge */}
              {!isLoading && insights.length > 0 && (
                <div className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-full text-sm shadow-md animate-pulse">
                  <span>{insights.length}</span>
                  <span className="text-purple-200 font-normal">insight{insights.length !== 1 ? 's' : ''}</span>
                </div>
              )}
              {!isLoading && insights.length === 0 && (
                <div className="px-3 py-1.5 bg-gray-100 text-gray-500 font-medium rounded-full text-sm">
                  No insights yet
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  generateInsights();
                }}
                disabled={isGenerating}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:bg-gray-400 transition-all shadow-md"
              >
                <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                {isGenerating ? 'Analyzing...' : 'Analyze'}
              </button>
              <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </button>

        {/* Insights List - Collapsible */}
        {isExpanded && (
          <div className="p-6">
          {insights.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 mx-auto text-purple-400 mb-3" />
              <p className="text-gray-600">No insights generated yet</p>
              <p className="text-sm text-gray-500 mt-1">Click "Analyze Data" to generate insights</p>
            </div>
          ) : (
            <div className="space-y-3">
              {insights.map((insight) => {
                const Icon = getCategoryIcon(insight.category);
                const platformColor = getPlatformColor(insight.platform?.type);
                
                return (
                  <div
                    key={insight.id}
                    onClick={() => setSelectedInsight(insight)}
                    className="bg-white rounded-lg border-2 border-gray-200 p-4 hover:border-purple-300 hover:shadow-lg transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-3">
                      {/* Icon */}
                      <div
                        className="p-2 rounded-lg flex-shrink-0"
                        style={{ backgroundColor: platformColor.light }}
                      >
                        <Icon className="w-5 h-5" style={{ color: platformColor.primary }} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 text-sm">{insight.title}</h4>
                          {/* Priority Badge */}
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${getPriorityStyle(insight.priority)}`}>
                            {insight.priority.toUpperCase()}
                          </span>
                        </div>

                        <p className="text-sm text-gray-700 mb-2">{insight.description}</p>

                        <div className="flex items-center gap-3 text-xs">
                          {/* Platform Badge */}
                          {insight.platform ? (
                            <div
                              className="px-2 py-1 rounded-md border font-semibold"
                              style={{
                                backgroundColor: platformColor.light,
                                borderColor: platformColor.medium,
                                color: platformColor.primary
                              }}
                            >
                              {insight.platform.name}
                            </div>
                          ) : (
                            <div className="px-2 py-1 bg-gradient-to-r from-green-100 to-lime-100 text-green-800 border border-green-300 rounded-md font-semibold">
                              All Platforms
                            </div>
                          )}

                          {/* Dollar Impact */}
                          {insight.dollar_impact !== null && insight.dollar_impact > 0 && (
                            <div className="flex items-center gap-1 text-green-700 font-semibold">
                              <DollarSign className="w-3 h-3" />
                              ${insight.dollar_impact.toFixed(0)} impact
                            </div>
                          )}

                          {/* Confidence */}
                          {insight.confidence_score !== null && (
                            <div className="text-gray-600">
                              {insight.confidence_score.toFixed(0)}% confidence
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Expand Arrow */}
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          {insights.length > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-200 text-center text-xs text-gray-600">
              Showing {insights.length} insight{insights.length !== 1 ? 's' : ''} for {startDate} to {endDate}
            </div>
          )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedInsight && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedInsight(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold">{selectedInsight.title}</h3>
              <button
                onClick={() => setSelectedInsight(null)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getPriorityStyle(selectedInsight.priority)}`}>
                  {selectedInsight.priority.toUpperCase()} PRIORITY
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 border border-purple-300 rounded-full text-xs font-semibold">
                  {selectedInsight.category.replace('_', ' ').toUpperCase()}
                </span>
                {selectedInsight.platform && (
                  <span
                    className="px-3 py-1 rounded-full text-xs font-semibold border"
                    style={{
                      backgroundColor: getPlatformColor(selectedInsight.platform.type).light,
                      borderColor: getPlatformColor(selectedInsight.platform.type).medium,
                      color: getPlatformColor(selectedInsight.platform.type).primary
                    }}
                  >
                    {selectedInsight.platform.name}
                  </span>
                )}
              </div>

              {/* Description */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What's Happening</h4>
                <p className="text-gray-700">{selectedInsight.description}</p>
              </div>

              {/* Recommendation */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Recommended Action</h4>
                <p className="text-gray-700">{selectedInsight.recommendation}</p>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-2 gap-4">
                {selectedInsight.dollar_impact !== null && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-xs text-green-700 font-semibold mb-1">POTENTIAL IMPACT</p>
                    <p className="text-2xl font-bold text-green-900">${selectedInsight.dollar_impact.toFixed(0)}</p>
                  </div>
                )}
                {selectedInsight.confidence_score !== null && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-xs text-blue-700 font-semibold mb-1">CONFIDENCE</p>
                    <p className="text-2xl font-bold text-blue-900">{selectedInsight.confidence_score.toFixed(0)}%</p>
                  </div>
                )}
              </div>

              {/* Related Info */}
              {(selectedInsight.related_sku || selectedInsight.related_customer_id) && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Related To</h4>
                  {selectedInsight.related_sku && (
                    <p className="text-sm text-gray-700">SKU: {selectedInsight.related_sku}</p>
                  )}
                  {selectedInsight.related_customer_id && (
                    <p className="text-sm text-gray-700">Customer ID: {selectedInsight.related_customer_id}</p>
                  )}
                </div>
              )}

              {/* Date Range */}
              <div className="text-xs text-gray-500">
                Analysis period: {selectedInsight.date_range_start} to {selectedInsight.date_range_end}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
